type Token<V extends string | number = string | number> = { value: V };

/**
 * Converts a `path -> value` map into Panda `defineTokens` shape.
 * Supports any token category (colors, fontWeights, spacing, …):
 * - leaf values may be `string` or `number`
 * - dotted paths (`'blue.main'`) become nested objects
 * - already-nested objects are converted recursively
 */
export function toPandaToken<V extends string | number = string | number>(
  record: Record<string, unknown>,
): Record<string, Token<V>> {
  const tokens: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string' || typeof value === 'number') {
      const parts = key.split('.');
      const leaf = parts.pop()!;
      const parent = parts.reduce<Record<string, unknown>>((acc, part) => {
        const existing = acc[part];
        if (!existing || typeof existing !== 'object' || 'value' in existing) {
          acc[part] = {};
        }
        return acc[part] as Record<string, unknown>;
      }, tokens);
      parent[leaf] = { value };
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      tokens[key] = toPandaToken<V>(value as Record<string, unknown>);
      continue;
    }
    throw new Error(`Invalid token combination for ${key}: ${value}`);
  }

  // Panda's Recursive<Token<…>> doesn't accept a hand-rolled recursive index
  // signature without a cast (same pattern as spacing tokens).
  return tokens as Record<string, Token<V>>;
}
